import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type CycleCountLineRecord = {
  id: string;
  cycleCountTaskId: string;
  itemId: string;
  itemName: string;
  itemSku: string | null;
  zoneId: string | null;
  stockAccountId: string;
  stockAccountName: string;
  expectedQuantity: number | null;
  expectedQuantityUnit: string;
  countedQuantity: number | null;
  countedQuantityUnit: string | null;
  varianceQuantity: number | null;
  variancePercentage: number | null;
  toleranceThreshold: number;
  withinTolerance: boolean | null;
  adjustmentStatus: string | null;
  unitCostOverride: number | null;
  recountRequested: boolean;
  countedBy: string | null;
  countedByUserName: string | null;
  countedAt: string | null;
  notes: string | null;
};

export type CycleCountTaskDetailRecord = {
  id: string;
  taskNumber: string;
  facilityId: string;
  status: string;
  countType: string;
  assignedTo: string | null;
  assignedToUserName: string | null;
  isBlindCount: boolean;
  isFrozen: boolean;
  dueDate: string | null;
  startedAt: string | null;
  completedAt: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  approvedByUserName: string | null;
  notes: string | null;
  lines: CycleCountLineRecord[];
  linesTotalCount: number;
  createdAt: string;
  updatedAt: string;
};

export const cycleCountTaskDetailQueryAtom = createQueryAtomFactory(
  'inventory.cycleCountTaskDetail',
  (params: { id: string; linesLimit?: number; linesOffset?: number }) => ({
    cycleCountTask: {
      __args: {
        id: params.id,
      },
      id: true,
      taskNumber: true,
      facilityId: true,
      status: true,
      countType: true,
      assignedTo: true,
      assignedToUser: {
        id: true,
        firstName: true,
        lastName: true,
      },
      isBlindCount: true,
      isFrozen: true,
      dueDate: true,
      startedAt: true,
      completedAt: true,
      approvedAt: true,
      approvedBy: true,
      approvedByUser: {
        id: true,
        firstName: true,
        lastName: true,
      },
      notes: true,
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
    cycleCountLines: {
      __args: {
        filter: {
          cycleCountTaskIds: [params.id],
        },
        pagination: {
          limit: params.linesLimit ?? 100,
          offset: params.linesOffset ?? 0,
        },
      },
      entries: {
        id: true,
        cycleCountTaskId: true,
        itemId: true,
        item: {
          id: true,
          name: true,
          skuNumber: true,
        },
        zoneId: true,
        stockAccountId: true,
        stockAccount: {
          id: true,
          name: true,
        },
        expectedQuantity: true,
        expectedQuantityUnit: true,
        countedQuantity: true,
        countedQuantityUnit: true,
        varianceQuantity: true,
        variancePercentage: true,
        toleranceThreshold: true,
        withinTolerance: true,
        adjustmentStatus: true,
        unitCostOverride: true,
        recountRequested: true,
        countedBy: true,
        countedByUser: {
          id: true,
          firstName: true,
          lastName: true,
        },
        countedAt: true,
        notes: true,
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): CycleCountTaskDetailRecord | null => {
      const task = data?.cycleCountTask;
      if (!task) {
        return null;
      }

      const lines: CycleCountLineRecord[] = (
        data?.cycleCountLines?.entries ?? []
      ).map((line) => ({
        id: line.id,
        cycleCountTaskId: line.cycleCountTaskId,
        itemId: line.itemId || line.item?.id || '',
        itemName: line.item?.name || '',
        itemSku: line.item?.skuNumber || null,
        zoneId: line.zoneId || null,
        stockAccountId: line.stockAccountId || '',
        stockAccountName: line.stockAccount?.name || '',
        expectedQuantity:
          line.expectedQuantity != null ? Number(line.expectedQuantity) : null,
        expectedQuantityUnit: line.expectedQuantityUnit || 'Pounds',
        countedQuantity:
          line.countedQuantity != null ? Number(line.countedQuantity) : null,
        countedQuantityUnit: line.countedQuantityUnit || null,
        varianceQuantity:
          line.varianceQuantity != null ? Number(line.varianceQuantity) : null,
        variancePercentage:
          line.variancePercentage != null
            ? Number(line.variancePercentage)
            : null,
        toleranceThreshold: Number(line.toleranceThreshold) || 0,
        withinTolerance: line.withinTolerance ?? null,
        adjustmentStatus: line.adjustmentStatus || null,
        unitCostOverride:
          line.unitCostOverride != null ? Number(line.unitCostOverride) : null,
        recountRequested: line.recountRequested,
        countedBy: line.countedBy || null,
        countedByUserName: line.countedByUser
          ? `${line.countedByUser.firstName ?? ''} ${line.countedByUser.lastName ?? ''}`.trim()
          : null,
        countedAt: line.countedAt || null,
        notes: line.notes || null,
      }));

      return {
        id: task.id,
        taskNumber: task.taskNumber,
        facilityId: task.facilityId,
        status: task.status,
        countType: task.countType,
        assignedTo: task.assignedTo || null,
        assignedToUserName: task.assignedToUser
          ? `${task.assignedToUser.firstName ?? ''} ${task.assignedToUser.lastName ?? ''}`.trim()
          : null,
        isBlindCount: task.isBlindCount,
        isFrozen: task.isFrozen,
        dueDate: task.dueDate || null,
        startedAt: task.startedAt || null,
        completedAt: task.completedAt || null,
        approvedAt: task.approvedAt || null,
        approvedBy: task.approvedBy || null,
        approvedByUserName: task.approvedByUser
          ? `${task.approvedByUser.firstName ?? ''} ${task.approvedByUser.lastName ?? ''}`.trim()
          : null,
        notes: task.notes || null,
        lines,
        linesTotalCount: data?.cycleCountLines?.page?.total || 0,
        createdAt: task.timestamps?.createdAt || '',
        updatedAt: task.timestamps?.updatedAt || '',
      };
    },
  },
);
